<template>
    <div>
        <Settings></Settings>
    </div>
</template>

<script>
import Overlay from 'ol/Overlay';
import {Cluster, Vector as VectorSource} from 'ol/source';
import {Circle as CircleStyle, Fill, Stroke, Style, Text} from 'ol/style';
import WKT from 'ol/format/WKT'
import AnimatedCluster from "ol-ext/layer/AnimatedCluster";
import SelectCluster from "ol-ext/interaction/SelectCluster";
import FontSymbol from "ol-ext/style/FontSymbol";
import Photo from "ol-ext/style/Photo";
import Settings from "./Settings";
import iziToast from "izitoast";
import {hexToRGBA, removeHashedIdFromURL} from "@/assets/js/utils";

import {missiles} from "@/animations/war/missiles";
import {airstrike} from "@/animations/war/airstrike";
import {drone} from "@/animations/war/drone";
import {earthquake} from "@/animations/nature/earthquake";
import {wildfire} from "@/animations/nature/wildfire";
import {volcano} from "@/animations/nature/volcano";
import {Vector as VectorLayer} from "ol/layer";
import { decryptResponse } from '@/utils/decrypt';
import {getCity} from "@/utils/cities";
import {mapState} from "@/assets/js/mapState";

const ANIMATIONS = {
    missiles,
    airstrike,
    drone,
    earthquake,
    wildfire,
    volcano
};

export default {
    name: "World",
    components: {
        Settings
    },
    data() {
        return {
            map: this.$store.getters["map/getMap"],
            cityLayers: {},
            customLayers: null,
            requestedFeature: null,
            featureCacheStyle: {},
            clusterSelectCacheStyle: {}
        };
    },
    mounted() {
        this.requestedFeature = {
            id: this.$route.params.hashed_id,
            feature: null
        };
        this.initializeWorld();
        this.setupMapActions();
    },
    methods: {
        // Init
        initializeWorld: function () {
            this.customLayers = new VectorLayer({
                id: -1,
                source: new VectorSource()
            });
            this.map.addLayer(this.customLayers);
            const loader = this.$loading.show({
                loader: "dots",
            });
            this.$http
                    .get(`${this.$serverUrl}/map/events`)
                    .then(async response => {
                        if (response.status === 200) {
                            let decryptedResponse = JSON.parse(decryptResponse(response.data));
                            this.$parent.lastUpdate = decryptedResponse.last_update;
                            await this.processNewEvents(decryptedResponse.events, true);
                        }
                    })
                    .catch(() => {})
                    .finally(() => {
                        loader.hide();
                    });
            setInterval(this.fetchUpdates, 10000)
        },
        fetchUpdates () {
            if (this.$parent.currentMenuId !== 1) {
                return;
            }
            this.$http
                    .get(`${this.$serverUrl}/map/events/update?lastUpdate=${this.$parent.lastUpdate}`)
                    .then(async response => {
                        if (response.status === 200) {
                            let decryptedResponse = JSON.parse(decryptResponse(response.data));
                            if (decryptedResponse.events && decryptedResponse.events.length > 0) {
                                this.$parent.lastUpdate = decryptedResponse.last_update;
                                await this.processNewEvents(decryptedResponse.events, false, true);
                            }
                        }
                    })
                    .catch(err => {
                        console.error('Error processing update events:', err);
                    })
        },
        processNewEvents: async function (events, handleRequestedFeature, isUpdate = false) {
            for (const event of events) {
                let feature;
                try {
                    feature = await this.createFeature(event);
                } catch (error) {
                    console.error('Skipping event that failed to build:', event && event.id, error);
                    continue;
                }
                if (!feature) {
                    continue;
                }
                this.addToMap(feature, true, isUpdate);
                if (handleRequestedFeature && this.requestedFeature.id === feature.get('id')) {
                    let outer = this;
                    setTimeout(function () {
                        outer.showOnMap(feature);
                    }, 1200)
                }
            }
        },
        setupMapActions: function () {
            let outer = this;

            // Select interaction to spread cluster out and select features
            const selectCluster = new SelectCluster({
                pointRadius: 20,
                animate: true,
                spiral: true,
                selectCluster: true,
                preserveFeatureSelection: true,
                filter: function (e) {
                    let features = e.get('features');
                    return features && features[0] && features[0].get('selectable') !== false;
                },
                featureStyle: function (feature, resolution) {
                    return outer.showFeaturesInClusterStyle(feature, resolution);
                },
                style: function (feature, resolution) {
                    return outer.clickFeaturesStyles(feature, resolution);
                }
            });
            mapState.selectCluster = selectCluster;
            this.map.addInteraction(selectCluster);

            // When selecting an object don't deselect the cluster
            selectCluster.on('select', function(e) {
                if (e.selected.length <= 0) {
                    e.preventDefault();
                    removeHashedIdFromURL();
                    return;
                }
                if (e.selected[0].get('selectclusterfeature')) {
                    if (e.deselected[0]) selectCluster.getFeatures().push(e.deselected[0])
                }
            });

            this.map.on("singleclick", (e) => {
                const feature = outer.map.forEachFeatureAtPixel(e.pixel, feature => feature);
                if (!feature) {
                    e.preventDefault();
                    $("#popup-closer").trigger("click");
                    mapState.selectCluster.clear();
                    return;
                }
                if (feature.get('selectable') === false) {
                    e.preventDefault();
                    return;
                }
                outer.map.getView().animate({center: feature.get('where') || e.coordinate, duration: 250});
                setTimeout(function () {
                    outer.createOverlay(feature);
                }, 300);
            });

            document.addEventListener('iziToast-closed', () => this.queueWork());

            // Click on map features
            $(document).on("click", "#newsOnMapBtn", (e) => {
                e.preventDefault();
                const coordinates = [...$(e.currentTarget).data("where").split(",")];
                outer.map.getView().animate({center: coordinates, duration: 1500, zoom: 12});
            });

            // Click on overlay items
            $(document).on("click", ".message", (e) => {
                e.preventDefault();
                if (!$(e.currentTarget).data("uid")) {
                    return;
                }
                mapState.slideRight.open();
                const notificationElement = document.getElementById("notification-" + $(e.currentTarget).data("uid"));
                $("#c-menu--slide-right").animate({scrollTop: notificationElement.offsetTop - 57}, 'slow');
            });

            this.map.getView().on('change:resolution', function () {
                $("#popup-closer").trigger("click");
            });
        },
        createOverlay: function (feature) {
            const map = this.map;

            this.$emit('create-overlay', "400px");

            map.getOverlays().getArray().slice(0).forEach(function (o) {
                map.removeOverlay(o);
            });
            $(".ol-popup").show();

            const overlay = new Overlay({
                element: mapState.container,
                autoPan: true,
                autoPanMargin: 20,
                autoPanAnimation: {
                    duration: 50
                }
            });
            mapState.overlay = overlay;
            map.addOverlay(overlay);

            let where;
            let title = "اخبار";
            if (feature.get("geometryType") === 'Point' || (feature.get('features') && feature.get('features')[0].get('geometryType') === 'Point')) {
                if (feature.get('cityId') || feature.get('features')[0].get('cityId')) {
                    const city = feature.get('cityId') ? getCity(feature.get('cityId')) : getCity(feature.get('features')[0].get('cityId'));
                    title = title + ' ' + city['title'];
                }
            }
            const content = mapState.content;
            content.innerHTML = `
                <div class="text-center sticky-top bg-light py-2 rounded">
                    <h5 class="mb-0"> ${title} </h5>
                </div>
            `;
            $(content).append('<div class="overlayMessage pt-2 px-2"></div>');
            const appendMessage = (data) => {
                const id = data.id;
                const image = data.image;
                const title = data.title || '';
                $(content).find('.overlayMessage').append(`
                    <div class="message"${id ? ` data-uid="${id}"` : ''}>
                        ${image ? `<img src="${image}" class="img-thumbnail">` : ''}
                        ${title}
                    </div>
                `);
            };
            if (feature.get('features')) {
                where = feature.getGeometry().getCoordinates();
                const cFeatures = feature.get('features');
                const reversed = cFeatures.slice().reverse();
                reversed.slice(0, 3).forEach(cFeature => {
                    appendMessage({
                        id: cFeature.get('id'),
                        image: cFeature.get('image') || '/images/placeholder.png',
                        title: cFeature.get('title')
                    });
                });
                const extraCount = reversed.length - 3;
                if (extraCount > 0) {
                    appendMessage({
                        title: `+ ${extraCount} خبر دیگر`
                    });
                }
            } else {
                where = feature.get('where');
                appendMessage({
                    id: feature.get('id'),
                    image: feature.get('image') || '/images/placeholder.png',
                    title: feature.get('title')
                });
            }
            setTimeout(function () {
                mapState.overlay.setPosition(where);
            }, 150);
        },
        createFeature: async function (news) {
            let feature;
            if (news.geometrySlug) {
                const customFeature = await this.createCustomFeature(news);
                if (!customFeature) {
                    return null;
                }
                feature = customFeature;
                this.setFeatureProperties(feature, news, news.geometrySlug);
            } else {
                feature = new WKT().readFeatures(news.geometry)[0];
                this.setFeatureProperties(feature, news, feature.getGeometry().getType());
            }
            return feature;
        },
        createCustomFeature: async function (news) {
            const [type, dataKey] = news.geometrySlug.split('-');
            const animationFn = ANIMATIONS[type];
            if (!animationFn) {
                console.warn(`Unknown animation type: ${type}`);
                return null;
            }
            try {
                const json = await import(`@/data/custom_features/${dataKey}.json`);
                return animationFn(this.customLayers, news, json.default).getFeature();
            } catch (error) {
                console.error(`Error loading animation '${news.geometrySlug}':`, error);
                return null;
            }
        },
        setFeatureProperties: function (feature, news, geometryType) {
            let geometry = feature.getGeometry();
            let where;
            if (geometryType === "Point") {
                where = geometry.getCoordinates();
            } else if (geometryType === "LineString") {
                let lineCoordinates = geometry.getCoordinates();
                let start = lineCoordinates[0];
                let end = lineCoordinates[lineCoordinates.length - 1];
                let midX = (start[0] + end[0]) / 2;
                let midY = (start[1] + end[1]) / 2;
                where = [midX, midY];
            } else if (geometryType === "Polygon") {
                where = geometry.getInteriorPoint().getCoordinates();
            } else {
                where = feature.get('where');
            }
            feature.setProperties({
                id: news.id,
                title: news.title,
                description: news.description,
                slug: news.slug,
                image: news.image,
                link: news.link,
                date: news.date,
                icon: news.icon,
                color: news.color,
                geometryType: geometryType,
                where: where,
                cityId: news.cityId,
                newsAgencyId: news.newsAgencyId
            });
        },
        addToMap: function (feature, showToast = false, isUpdate = false) {
            let outer = this;
            if (feature.get('geometryType') === "Point") {
                this.map.addLayer(outer.makePointsCluster(feature));
            } else if (feature.get('geometryType') === "LineString") {
                outer.addLineStringToMap(feature);
            } else if (feature.get('geometryType') === "Polygon") {
                outer.addPolygonToMap(feature)
            }
            this.addToNotificationBar(feature, isUpdate);
            if (showToast) {
                this.$parent.toastsQueue.push(feature);
                this.queueWork();
            }
        },
        addToNotificationBar: function (feature, isUpdate = false) {
            this.$parent.notificationsCount++;

            const id = feature.get('id');
            const title = feature.get('title') || '';
            const description = feature.get('description') || '';
            const image = feature.get('image') || '';
            const date = new persianDate(new Date(feature.get('date'))).format("L");
            const slug = feature.get('slug') || '';

            const wrapper = document.createElement('div');
            wrapper.className = 'notification w-100 my-2';
            wrapper.id = `notification-${id}`;

            wrapper.innerHTML = `
                <div class="notif-card">
                    <div class="notif-img-wrap">
                        ${image
                            ? `<img class="notif-img" src="${image}" alt="${title}" loading="lazy" onerror="this.style.display='none'" />`
                            : `<div style="width:100%;height:100%;background:linear-gradient(135deg,#e0f2fe,#ddd6fe);"></div>`}
                        <div class="notif-date-badge">
                             <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                                 stroke-linecap="round" stroke-linejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            ${date}
                        </div>
                    </div>
                    <div class="notif-body">
                        ${isUpdate ? '<span class="new-badge">جدید</span>' : ''}
                        <div class="notif-title">${title}</div>
                        <div class="notif-desc">${description}</div>
                    </div>
                    <div class="notif-actions">
                        <button class="btn-action map-btn" data-id="${id}">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
                                <line x1="8" y1="2" x2="8" y2="18"/>
                                <line x1="16" y1="6" x2="16" y2="22"/>
                            </svg>
                            نقشه
                        </button>
                        <a class="btn-action" href="/blog/${slug}" target="_blank">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                <polyline points="15 3 21 3 21 9"/>
                                <line x1="10" y1="14" x2="21" y2="3"/>
                            </svg>
                            بیشتر
                        </a>
                    </div>
                </div>
            `;

            wrapper.querySelector('.map-btn').addEventListener('click', () => {
                mapState.slideRight.close();
                this.showOnMap(feature);
            });

            isUpdate
                    ? $("#notifications-body").prepend(wrapper)
                    : $("#notifications-body").append(wrapper);
        },
        queueWork: function () {
            let amount = 1;
            if (window.innerHeight >= 900) {
                amount = 3;
            } else if (window.innerHeight >= 768) {
                amount = 2;
            }
            const toasts = document.querySelectorAll(".iziToast");
            if (toasts.length >= amount || this.$parent.toastsQueue.length === 0) return;
            const showToast = (feature) => {
                iziToast.show({
                    class: 'iziToast-Map',
                    rtl: true,
                    theme: 'dark',
                    title: feature.get('title'),
                    position: 'bottomRight',
                    transitionIn: 'fadeInUp',
                    transitionOut: 'fadeOutDown',
                    drag: false,
                    progressBarColor: '#ffffff',
                    iconColor: '#6c63ff',
                    timeout: 6000,
                    image: feature.get('image'),
                    imageWidth: 80,
                    layout: 2,
                    close: false,
                    buttons: [
                        [
                            '<button class="float-left"><b> نقشه </b></button>',
                            () => {
                                this.showOnMap(feature);
                            }
                        ],
                        [
                            `<div class="float-right text-white mr-1 mt-1 ltr">
                        <small>${new persianDate(new Date(feature.get('date'))).format("L HH:mm")}</small>
                        </div>`
                        ]],
                    onOpening: () => {},
                    onOpened: () => {},
                    onClosing: () => {},
                    onClosed: () => {}
                });
            };
            while (document.querySelectorAll(".iziToast").length < amount && this.$parent.toastsQueue.length > 0) {
                showToast(this.$parent.toastsQueue.shift());
            }
        },
        showOnMap: function (feature) {
            this.requestedFeature = {
                id: feature.get("id"),
                feature: feature
            };
            this.handleHashedId();
        },
        handleHashedId: function () {
            let outer = this;
            let requestedFeature = this.requestedFeature;
            if (requestedFeature.feature === null) {
                return;
            }
            let layer;
            let zoom = 16;
            if (requestedFeature.feature.get('geometryType') === "Point") {
                layer = outer.cityLayers[requestedFeature.feature.get('cityId')];
            } else {
                layer = this.customLayers;
                zoom = 6;
            }
            this.map.getView().animate({
                center: requestedFeature.feature.get('where'),
                duration: 1400,
                zoom: zoom
            });
            function flattenFeatures(features) {
                let result = [];
                for (const f of features) {
                    const cluster = f.get('features');
                    if (cluster && cluster.length) {
                        result.push(...flattenFeatures(cluster));
                    } else {
                        result.push(f);
                    }
                }
                return result;
            }
            const allFeatures = flattenFeatures(layer.getSource().getFeatures());
            const foundFeature = allFeatures.find(f => requestedFeature.id === f.get('id'));
            setTimeout(() => {
                const parentCluster = layer.getSource().getFeatures().find(cluster => {
                    const clusterFeatures = cluster.get('features');
                    return clusterFeatures && clusterFeatures.includes(foundFeature);
                });
                if (parentCluster) {
                    mapState.selectCluster.getFeatures().clear();
                    mapState.selectCluster.getFeatures().push(parentCluster);
                    mapState.selectCluster.selectCluster(parentCluster);
                }
            }, 1450);
            setTimeout(() => outer.createOverlay(foundFeature), 2000);
        },
        // Make Cluster Method
        makePointsCluster: function (feature) {
            let outer = this;
            let cityId = feature.get('cityId');
            let clusterFeatures = [feature];
            if (this.cityLayers[cityId]) {
                this.cityLayers[cityId].getSource().getSource().getFeatures().forEach(feature => {
                    clusterFeatures.push(feature)
                });
                this.map.removeLayer(this.cityLayers[cityId]);
            }
            let clusterSource = new Cluster({
                distance: 50,
                source: new VectorSource({
                    features: clusterFeatures
                })
            });
            return this.cityLayers[cityId] = new AnimatedCluster({
                cityId: cityId,
                source: clusterSource,
                style: function (feature, resolution) {
                    return outer.showFeaturesStyles(feature, resolution);
                }
            });
        },
        addLineStringToMap: function (feature) {
            feature.setStyle(new Style({
                stroke: new Stroke({
                    color: feature.get('color'),
                    width: 2
                })
            }));
            this.customLayers.getSource().addFeature(feature);
        },
        addPolygonToMap: function (feature) {
            feature.setStyle(new Style({
                stroke: new Stroke({
                    color: feature.get('color'),
                    width: 2
                }),
                fill: new Fill({
                    color: hexToRGBA(feature.get('color'), 0.1)
                })
            }));
            this.customLayers.getSource().addFeature(feature);
        },
        // Styles
        showFeaturesStyles: function (feature, resolution) {
            let outer = this;
            let features = feature.get('features');
            if (features.length === 1) {
                let eachFeature = feature.get('features');
                let id = eachFeature[0].get('id');
                let image = eachFeature[0].get('image');
                let color = eachFeature[0].get('color');
                let style = outer.featureCacheStyle[id];
                if (!style) {
                    if (image !== "") {
                        style = outer.featureCacheStyle[id] = new Style({
                            image: new Photo({
                                src: image,
                                kind: 'anchored',
                                radius: 10,
                                crop: true,
                                shadow: true,
                                displacement: [0, 0],
                                stroke: new Stroke({
                                    width: 2,
                                    color: color
                                })
                            })
                        })
                    } else {
                        style = outer.featureCacheStyle[id] = new Style({
                            image: new FontSymbol({
                                form: 'circle',
                                glyph: eachFeature[0].get('icon'),
                                radius: 14,
                                fontSize: 0.7,
                                color: '#fff',
                                fill: new Fill({
                                    color: color
                                }),
                                stroke: new Stroke({
                                    color: color,
                                    width: 1
                                })
                            })
                        })
                    }
                }
                return [style];
            } else { // Cluster Style
                return new Style({
                    image: new FontSymbol({
                        form: "hexagon",
                        radius: 12,
                        fill: new Fill({
                            color: "rgb(133,127,255)"
                        }),
                        stroke: new Stroke({
                            color: "rgba(108, 99, 255, 1)",
                            width: 1
                        })
                    }),
                    text: new Text({
                        text: features.length.toString(),
                        fill: new Fill({
                            color: '#fff'
                        }),
                        font: 'bold 14px Arial'
                    })
                });
            }
        },
        clickFeaturesStyles: function (feature, resolution) {
            let cluster = feature.get('features');
            if (cluster.length > 1) {
                return [this.clickClusterSelectStyle(feature, resolution)];
            } else {
                let eachFeature = feature.get("features");
                let image = eachFeature[0].get('image');
                let color = eachFeature[0].get('color');
                if (image !== "") {
                    return [
                        new Style({
                            image: new Photo({
                                src: image,
                                kind: 'circle',
                                radius: 12,
                                crop: true,
                                shadow: true,
                                displacement: [0, 0],
                                stroke: new Stroke({
                                    width: 2,
                                    color: color
                                })
                            })
                        })
                    ];
                }
                return [
                    new Style({
                        image: new FontSymbol({
                            form: 'circle',
                            glyph: feature.get("features")[0].get("icon"),
                            radius: 14,
                            fontSize: 0.7,
                            color: '#fff',
                            fill: new Fill({
                                color: color
                            }),
                            stroke: new Stroke({
                                color: color,
                                width: 2
                            })
                        })
                    })
                ];
            }
        },
        clickClusterSelectStyle: function (feature, resolution) {
            let size = feature.get('features').length;
            let style = this.clusterSelectCacheStyle[size];
            if (!style) {
                let color = size > 25 ? "192,0,0" : size > 8 ? "255,128,0" : "0,128,0";
                style = this.clusterSelectCacheStyle[size] = new Style({
                    image: new CircleStyle({
                        radius: Math.max(8, Math.min(size * 0.75, 20)),
                        stroke: new Stroke({
                            color: `rgba(${color}, 0.6)`,
                            width: 3,
                            lineCap: 'round'
                        }),
                        fill: new Fill({
                            color: `rgba(${color}, 0.7)`
                        })
                    }),
                    text: new Text({
                        text: size.toString(),
                        font: 'bold 14px',
                        fill: new Fill({
                            color: '#fff'
                        }),
                        stroke: new Stroke({
                            color: '#000',
                            width: 2
                        })
                    })
                });
            }
            return style;
        },
        showFeaturesInClusterStyle: function (feature, resolution) {
            let features = feature.get("features");
            if (!features || features.length === 0) {
                return [];
            }
            if (features[0].get('image') !== "") {
                return [
                    new Style({
                        image: new Photo({
                            src: features[0].get('image'),
                            kind: 'circle',
                            radius: 10,
                            crop: true,
                            shadow: true,
                            displacement: [0, 0],
                            stroke: new Stroke({
                                width: 1,
                                color: features[0].get('color')
                            })
                        })
                    })
                ];
            }
            return [
                new Style({
                    image: new FontSymbol({
                        form: 'circle',
                        glyph: feature.get("features")[0].get("icon"),
                        radius: 10,
                        fontSize: 0.7,
                        color: '#fff',
                        fill: new Fill({
                            color: feature.get("features")[0].get("color")
                        }),
                        stroke: new Stroke({
                            color: feature.get("features")[0].get("color"),
                            width: 1
                        })
                    })
                })
            ];
        },
    }
}
</script>

<style>
    .notification {
        padding: 0 20px 0;
        transition: .5s;
    }

    .notificationTitle {
        white-space: nowrap;
        text-overflow: ellipsis;
        width: 250px !important;
        overflow: hidden;
        margin-bottom: 10px;
        font-size: 13px;
    }

    .notificationImage {
        height: 180px !important;
        border-radius: 10% 10% 10% 10%
    }

    .message {
        cursor: pointer;
        position: relative !important;
        direction: rtl !important;
        text-align: right !important;
        white-space: nowrap;
        text-overflow: ellipsis;
        width: 100% !important;
        overflow: hidden;
        margin-bottom: 5px;
    }

    .message img {
        width: 20%;
        height: 50px;
        border-radius: 10px;
        margin: 2px 0 2px 5px;
    }

    .notif-card {
        background: #fff;
        border: 0.5px solid #e2e8f0;
        border-radius: 16px;
        overflow: hidden;
        transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;

        &:hover {
            transform: translateY(-2px);
            border-color: rgba(37, 99, 235, 0.2);
            box-shadow: 0 6px 20px rgba(37, 99, 235, 0.06);

            .notif-img { transform: scale(1.04); }
        }
    }

    .notif-img-wrap {
        position: relative;
        height: 140px;
        overflow: hidden;
        background: #f1f5f9;
    }

    .notif-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.4s ease;
        display: block;
    }

    .notif-date-badge {
        position: absolute;
        bottom: 8px;
        left: 8px;
        background: rgba(255, 255, 255, 0.92);
        backdrop-filter: blur(8px);
        border: 0.5px solid rgba(255, 255, 255, 0.6);
        border-radius: 8px;
        padding: 3px 8px;
        font-size: 11px;
        font-weight: 600;
        direction: ltr;
        display: flex;
        align-items: center;
        gap: 4px;
        svg {
            color: #2563eb;
            transform: translateY(-1px);
        }
    }

    .notif-body {
        padding: 12px 14px 8px;
    }

    .notif-title {
        font-size: 13px;
        font-weight: 700;
        line-height: 1.75;
        margin-bottom: 6px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .notif-desc {
        font-size: 11.5px;
        line-height: 1.8;
        color: #475569;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        margin-bottom: 10px;
    }

    .notif-actions {
        display: flex;
        gap: 6px;
        padding: 8px 14px 12px;
        border-top: 0.5px solid #e2e8f0;
    }

    .btn-action {
        flex: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        border: 0.5px solid #cbd5e1;
        border-radius: 8px;
        padding: 6px 10px;
        font-family: "Vazirmatn", sans-serif;
        font-size: 12px;
        font-weight: 600;
        color: #475569;
        background: transparent;
        cursor: pointer;
        text-decoration: none;
        transition: all 0.2s ease;

        &:hover {
            color: #2563eb;
            border-color: rgba(37, 99, 235, 0.25);
            background: rgba(37, 99, 235, 0.04);
        }

        &.map-btn:hover {
            color: #0891b2;
            border-color: rgba(8, 145, 178, 0.25);
            background: rgba(8, 145, 178, 0.04);
        }
    }

    .new-badge {
        display: inline-flex;
        align-items: center;
        background: rgba(37, 99, 235, 0.08);
        color: #1d4ed8;
        font-size: 10px;
        font-weight: 700;
        padding: 2px 7px;
        border-radius: 6px;
        margin-left: 6px;
    }
</style>

<style scoped>

</style>
